import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { useFamiliesWithClusters } from '../api/use-families-with-clusters';
import { useJobRoleDetailsQueries } from '../api/use-job-item-details';

// Resolves the label shown under a compared item: the cluster for a role, the parent role for a level.
// Roles come from the families tree in one request; levels need their parent roles fetched by id.
export const useParentTitles = (
    architectureId: string,
    itemType: 'job-level' | 'job-role',
    parentIds: readonly string[],
): ReadonlyMap<string, string> => {
    const { data: familiesWithClusters } = useFamiliesWithClusters(architectureId, {
        enabled: itemType === 'job-role',
    });
    const roleRequests = useMemo(
        () => (itemType === 'job-level' ? parentIds.map((itemId) => ({ itemId, version: undefined })) : []),
        [itemType, parentIds],
    );
    const parentRoleQueries = useJobRoleDetailsQueries({ architectureId, roleRequests });

    return useMemo(() => {
        if (itemType === 'job-role') {
            return new Map(
                (familiesWithClusters ?? []).flatMap((family) =>
                    family.clusters.map((cluster) => [cluster.id, cluster.title] as const),
                ),
            );
        }

        return new Map(
            parentRoleQueries.flatMap((query) =>
                is.undefined(query.data) ? [] : [[query.data.id, query.data.title] as const],
            ),
        );
    }, [familiesWithClusters, itemType, parentRoleQueries]);
};

export const uniqueParentIds = (parentIds: readonly (string | undefined)[]): readonly string[] => [
    ...new Set(parentIds.filter(is.string)),
];
